import React from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../context/LanguageContext';
import SEO from '../components/SEO';

const AIGovernance = () => {
  const { t } = useTranslation('aiGovernance');
  const { language } = useLanguage();

  // Governance principles aligned with ASEAN AI Governance and Ethics Guide
  const governance = [
    {
      title: t('principles.fairness.title'),
      description: t('principles.fairness.description'),
      tag: t('principles.fairness.tag')
    },
    {
      title: t('principles.safety.title'),
      description: t('principles.safety.description'),
      tag: t('principles.safety.tag')
    },
    {
      title: t('principles.privacy.title'),
      description: t('principles.privacy.description'),
      tag: t('principles.privacy.tag')
    },
    {
      title: t('principles.inclusiveness.title'),
      description: t('principles.inclusiveness.description'),
      tag: t('principles.inclusiveness.tag')
    },
    {
      title: t('principles.transparency.title'),
      description: t('principles.transparency.description'),
      tag: t('principles.transparency.tag')
    },
    {
      title: t('principles.accountability.title'),
      description: t('principles.accountability.description'),
      tag: t('principles.accountability.tag')
    },
    {
      title: t('principles.human.title'),
      description: t('principles.human.description'),
      tag: t('principles.human.tag')
    }
  ];

  return (
    <div className="governance-page pb-5 mb-5">
      <SEO 
        title="AI Governance Malaysia - Responsible AI Framework | ASEAN Aligned"
        description="EWANDS provides enterprise-ready AI governance aligned with ASEAN AI Governance and Ethics Guide. Trusted by law enforcement, government, and enterprise clients for responsible, compliant, and auditable AI deployment in Malaysia."
        keywords="AI governance Malaysia, responsible AI framework, ASEAN AI governance, law enforcement AI governance, AI compliance Malaysia, AI ethics enterprise, AI transparency, AI accountability, AI safety Malaysia, AI implementation governance"
        canonicalPath={`/${language}/ai-governance`}
        structuredData={{
          "@context": "https://schema.org",
          "@type": "Service",
          "name": "AI Governance Services",
          "provider": {
            "@type": "Organization",
            "name": "EWANDS SDN BHD"
          },
          "description": "Executive-ready Responsible AI governance framework aligned to ASEAN standards",
          "areaServed": "MY"
        }}
      />
      {/* Hero Section */}
      <section className="hero-section governance-hero">
        <div className="hero-background">
          <div className="hero-bg-image"></div>
          <div className="gradient-layer"></div>
          {/* <div className="particle-layer"></div> */}
          <div className="grid-overlay"></div>
          <div className="tech-lines"></div>
        </div>
        
        <div className="hero-content">
          <div className="container">
            <div className="hero-wrapper">
              <div className="tech-element tech-element-1"></div>
              <div className="tech-element tech-element-2"></div>
              <div className="tech-element tech-element-6"></div>
              <h1 className="hero-quote">
                <span className="quote-highlight">{t('hero.title')}</span><br />
                {t('hero.subtitle1')}<br />
                {t('hero.subtitle2')}
              </h1>
              <p className="hero-subtitle fade-in" style={{ animationDelay: '0.2s' }}>
                {t('hero.description')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Governance Intro - Teaser Style */}
      <section className="ai-agents-content">
        <div className="container">
          <div className="agents-intro">
            <div className="agents-intro-centered fade-in">
              <div className="card-tag">{t('intro.tag')}</div>
              <h2 className="intro-heading">{t('intro.heading')}</h2>
              <p className="intro-description">
                {t('intro.description1')}
              </p>
              <p className="intro-description">
                {t('intro.description2')}
              </p>
              <div className="scroll-indicator">
                <span className="scroll-text">{t('intro.scrollText')}</span>
                <span className="scroll-arrow">↓</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Governance Principles */}
      <section className="capabilities-grid-section" style={{ paddingTop: '3rem' }}>
        <div className="container">
          <div className="row g-4 mb-5">
            {governance.map((item, index) => {
              // Make Safety (index 1) and Accountability (index 5) take 2-box width
              const isLarge = index === 1 || index === 5;
              const colClass = isLarge ? 'col-lg-12 col-xl-8' : 'col-lg-6 col-xl-4';
              
              return (
                <div key={index} className={colClass}>
                  <div className="capability-card-modern fade-in" style={{ animationDelay: `${0.3 + index * 0.1}s` }}>
                    <div className="card-tag">{item.tag}</div>
                    <h2 className="capability-title-bold">{item.title}</h2>
                    <div className="card-divider"></div>
                    <p className="capability-desc-text">{item.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
};

export default AIGovernance;
